/**
 * THE SHIPPING CALENDAR, ONE FOR THE WHOLE STORE (2026-10-23, Alex: "a global calculation that every block can use")
 *
 * Every date the shop gives - when an order leaves, when it arrives, the cart line's "Estimated Shipping" - is worked
 * out here, from window.CF_SHIP (snippets/cf-ship-data, in the head of every page: the visitor's country, the inventory
 * and tax metaobjects). It runs in the browser because Shopify caches pages: a date printed by Liquid is the date of
 * whoever filled the cache.
 *
 *   window.cfShip.estimate(kind)  "Between November 4th and November 9th" - the made-to-order ship window as the cart
 *                                 line carries it (a FRAME, GRAND or BLOK); null for a kind made from stock
 *   window.cfShip.arrival(kind, serviceKey)  [Date, Date] - when that one service lands, the same windows `line`
 *                                 renders, for a caller that wants the dates rather than the sentence
 *   window.cfShip.line(page)      the product page's shipping line and Good to know's delivery times
 *                                 (snippets/cf-delivery-data); also window.cfDeliveryRender / SetKind / SetVariant /
 *                                 SetBasket and window.cfShipEstimate, which the configurators call
 *   the cart                      where the cart holds such a line (CF_SHIP.refresh), its window is rewritten to today's
 *
 * THE CALENDAR, all of it from the two metaobjects:
 * - Order day: today if it is a workshop day (Monday to Friday, not in inventory `holidays`) and Berlin time is before
 *   the cut-off, else the next workshop day. Which cut-off: made to order, the UPS hour (`order_cutoff_hour`) for
 *   every carrier - the workshop starts the order the same way whoever carries it later (Alex, 2026-10-24: "from the
 *   day of order"; until then a made-to-order parcel's DHL dates started a day after its UPS dates once DHL's cut-off
 *   had passed, about three days later in the end). From stock, the parcel leaves on the order day, so its own
 *   carrier's hour (DHL `order_cutoff_hour_dhl`); on the product page's line, the hour of the service the line names.
 *   Before 2026-10-23 the cart's estimate for GRAND and BLOK took DHL's hour in Germany while the page took UPS's: a
 *   day apart from 11 to 13.
 * - The line names the cheapest service, Express aside - or one just as fast (the same transit days) that arrives
 *   sooner because its carrier's cut-off is later (Alex, 2026-10-23: between DHL's cut-off at 11 and UPS's at 13 UPS
 *   Standard still goes today and arrives a day before DHL, so the line says UPS Standard and counts down to 13:00). A
 *   faster, dearer class is never named instead (UK: UPS Economy, not Standard). "Or as early as ... with UPS Express"
 *   only where Express beats every cheaper service.
 * - Ship days: from stock the order day; made to order the production window counted in calendar days from it, each
 *   end moved on to the next workshop day where it lands on a day off.
 * - Arrival: the service's transit days (tax `transit_*`, freight `transit_freight`) counted from the ship day on the
 *   days the carrier delivers there, skipping the country's `public_holidays`.
 */
(() => {
  'use strict';
  if (window.cfShip) return;
  const D = window.CF_SHIP;
  if (!D) return;

  const DAY = 86400000;
  const iso = (d) => d.toISOString().slice(0, 10);
  const plus = (d, n) => new Date(d.getTime() + n * DAY);
  const isoWeekday = (d) => d.getUTCDay() || 7;
  const fill = (s, vars) => String(s).replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => (vars[k] != null ? vars[k] : ''));

  const workshopOff = new Set(D.workshop_off || []);
  const destOff = new Set(D.dest_off || []);
  const workshopDay = (d) => isoWeekday(d) <= 5 && !workshopOff.has(iso(d));
  const isFreight = (kind) => (D.freight_kinds || []).includes(kind);

  const berlinNow = () => {
    const p = Object.fromEntries(new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit',
      day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(new Date()).map((x) => [x.type, x.value]));
    return { today: new Date(Date.UTC(+p.year, +p.month - 1, +p.day)), minutes: +p.hour * 60 + +p.minute };
  };
  const onOrAfter = (d) => {
    for (let i = 0; i < 60 && !workshopDay(d); i++) d = plus(d, 1);
    return d;
  };
  const cutoffOf = (carrier) => (D.cutoff[carrier] != null ? D.cutoff[carrier] : 12);
  const counting = (now, cutoff) => D.cutoff_on && workshopDay(now.today) && now.minutes < cutoff * 60;
  // The day the order counts as placed: today before the cut-off, else the next workshop day.
  const orderDay = (now, cutoff) => (counting(now, cutoff) ? now.today : onOrAfter(plus(now.today, 1)));
  const production = (kind) => {
    const p = D.production && D.production[kind];
    return Array.isArray(p) && p.length ? [p[0], p[p.length - 1]] : null;
  };
  // The days a parcel can leave: from stock the order day by its carrier's cut-off; made to order the production
  // window counted from the one order day every carrier shares, the UPS hour's (see the header).
  const shipWindow = (kind, cutoff, now) => {
    const p = production(kind);
    if (!p) {
      const base = orderDay(now, cutoff);
      return [base, base];
    }
    const base = orderDay(now, cutoffOf('ups'));
    return [onOrAfter(plus(base, p[0])), onOrAfter(plus(base, p[1]))];
  };
  const arrive = (from, n, weekdays) => {
    let d = from;
    for (let counted = 0, i = 0; counted < n && i < 120; i++) {
      d = plus(d, 1);
      if (weekdays.includes(isoWeekday(d)) && !destOff.has(iso(d))) counted++;
    }
    return d;
  };

  // A FRAME carton (73 x 72 x 24 cm) never fits DHL Paket, and UPS Economy never carries a FRAME (snippets/shipping-line).
  const FRAME_SKIP = ['transit_dhl', 'transit_ups_economy'];
  const skipFor = (kind) => (kind === 'frame' || kind === 'custom_frame' ? FRAME_SKIP : []);
  const servicesFor = (skip) => (D.services || []).filter((s) => !(skip || []).includes(`transit_${s.key}`));
  // Whose cut-off hour counts: UPS's for freight; else the carrier of the first service the parcel goes with.
  const cutoffCarrier = (kind, services) => {
    if (isFreight(kind)) return 'ups';
    const lead = services.find((s) => !s.express);
    return lead ? lead.carrier : 'ups';
  };

  // "November 4th" / "4. November", as the cart line has always written it.
  const longDate = (d) => {
    const day = d.getUTCDate();
    const month = (D.estimate.months || [])[d.getUTCMonth()] || '';
    if (D.estimate.en) {
      const suffix = [1, 21, 31].includes(day) ? 'st' : [2, 22].includes(day) ? 'nd' : [3, 23].includes(day) ? 'rd' : 'th';
      return `${month} ${day}${suffix}`;
    }
    return fill(D.estimate.day_month, { day, month });
  };

  // WHEN ONE SERVICE LANDS, as dates rather than as a sentence (2026-10-23). `line` renders the same windows and
  // writes them into the page; this returns them, so something other than the page can ask - the checkout's own
  // dates are built by running this calendar once per country rather than by carrying a second copy of it.
  //
  //   cfShip.arrival('stock', 'ups_standard')  ->  [Date, Date]
  //   cfShip.arrival('grand', 'freight')       ->  [Date, Date], the crate's own lane
  //
  // `serviceKey` is a tax `transit_*` key without the prefix - dhl, ups_economy, ups_standard, ups_expedited,
  // ups_express - or 'freight' for the kinds that go by it. null where the service does not apply to the kind (a
  // FRAME carton never goes by DHL) or the country has no days for it. Whether a cart may USE a service is not
  // asked here: the Economy band is a question about the cart, and in checkout the option is simply offered or
  // it is not.
  const arrival = (kind, serviceKey, now) => {
    now = now || berlinNow();
    // Freight and parcels never cross: the crate is how a GRAND and a BLOK travel, and the only way they do. Asking
    // for a parcel service on one of them, or for freight on anything else, is a question with no answer.
    if (isFreight(kind) !== (serviceKey === 'freight')) return null;
    if (isFreight(kind)) {
      const f = Array.isArray(D.freight) ? D.freight : [];
      if (!f.length) return null;
      const [a, b] = shipWindow(kind, cutoffOf('ups'), now);
      const wk = [1, 2, 3, 4, 5];
      return [arrive(a, f[0], wk), arrive(b, f[f.length - 1], wk)];
    }
    const s = servicesFor(skipFor(kind)).find((x) => x.key === serviceKey);
    if (!s || !Array.isArray(s.days) || !s.days.length) return null;
    const [a, b] = shipWindow(kind, cutoffOf(s.carrier), now);
    return [arrive(a, s.days[0], s.weekdays), arrive(b, s.days[s.days.length - 1], s.weekdays)];
  };

  // The made-to-order ship window as the cart line carries it, from the order day every carrier shares.
  const estimate = (kind) => {
    if (!production(kind)) return null;
    const [a, b] = shipWindow(kind, cutoffOf('ups'), berlinNow());
    return fill(D.estimate.t, { min_date: longDate(a), max_date: longDate(b) });
  };

  // ---------------------------------------------------------------------------------------------- the cart
  // A FRAME, GRAND or BLOK line keeps the window its product page or card showed when it was added - however long the
  // cart waited (order #2118098 went through with a window two weeks gone). On every page whose cart holds one, it is
  // worked out again for today and written onto the line where it differs (/cart/change.js), under this page's name for
  // the property. A GRAND line is the cart transform's: it rebuilds the visible rows from `_grand_meta`, so the copy is
  // rewritten with them. Read fresh from /cart.js, never from the page, which Shopify may hand out cached; checkout
  // itself cannot be reached.
  const kindOf = (handle) => {
    if (handle === 'grand') return 'grand';
    if (handle === 'blok') return 'blok';
    if (handle === 'frame' || handle === 'frame-discontinued' || handle === 'frame-wide') return 'frame';
    return null;
  };
  const root = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const refreshCart = async () => {
    const cart = await (await fetch(`${root()}cart.js`, { credentials: 'same-origin' })).json();
    const label = D.estimate.label;
    let changed = 0;
    for (const item of cart.items || []) {
      const props = item.properties || {};
      const key = Object.keys(props).find((k) => D.estimate.labels.includes(k));
      const kind = key && kindOf(item.handle);
      const text = kind && estimate(kind);
      if (!text || (key === label && props[key] === text)) continue;
      // Same rows in the same order, the window swapped in place under this page's name.
      const next = {};
      Object.keys(props).forEach((k) => {
        if (k === key) next[label] = text;
        else next[k] = props[k];
      });
      if (props._grand) next._grand_meta = JSON.stringify(Object.fromEntries(Object.entries(next).filter(([k]) => k.charAt(0) !== '_')));
      const res = await fetch(`${root()}cart/change.js`, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: item.key, quantity: item.quantity, properties: next })
      });
      if (!res.ok) break;
      changed++;
    }
    if (!changed) return;
    if (D.cart_page) {
      // Drawn by the server: once more, at most once a minute, so a refusal can never loop.
      let last = 0;
      try { last = +sessionStorage.getItem('cf-cart-ship-reload') || 0; } catch (e) {}
      if (Date.now() - last > 60000) {
        try { sessionStorage.setItem('cf-cart-ship-reload', String(Date.now())); } catch (e) {}
        location.reload();
      }
    } else {
      document.dispatchEvent(new Event('cart:refresh'));
    }
  };
  if (D.refresh) {
    const start = () => refreshCart().catch(() => {});
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
    else start();
  }

  // ---------------------------------------------------------------------------------------------- the product page
  // Line 1, in every country, says when it ships; line 2, where the country shows arrival dates
  // (snippets/cf-delivery-active), says when it arrives; Good to know's Delivery fact lists every service with its
  // dates. `page` is snippets/cf-delivery-data's: what the page sells (kind, skip), whether it shows dates, the
  // selected variant's price and weight for UPS Economy, and the sentences.
  const line = (P) => {
    if (window.cfDeliveryStarted) return;
    window.cfDeliveryStarted = true;

    const whole = (s) => `<span class="cf-delivery-date">${s}</span>`;
    let short, weekday;
    const as = { timeZone: 'UTC', calendar: 'gregory', numberingSystem: 'latn' };
    try {
      short = new Intl.DateTimeFormat(D.locale, { weekday: 'short', day: 'numeric', month: 'short', ...as });
      weekday = new Intl.DateTimeFormat(D.locale, { weekday: 'long', ...as });
    } catch (e) {
      short = new Intl.DateTimeFormat(undefined, { weekday: 'short', day: 'numeric', month: 'short', ...as });
      weekday = new Intl.DateTimeFormat(undefined, { weekday: 'long', ...as });
    }
    let rangeHasComma = true;
    try { rangeHasComma = /^\S+,/.test(short.formatRange(new Date(Date.UTC(2026, 9, 16)), new Date(Date.UTC(2026, 9, 17)))); } catch (e) {}
    const one = (d) => (rangeHasComma ? short.format(d) : short.format(d).replace(/^(\S+),/, '$1'));
    const span = (a, b) => {
      if (iso(a) === iso(b)) return one(a);
      try { return short.formatRange(a, b); } catch (e) { return `${short.format(a)} – ${short.format(b)}`; }
    };

    let kind = P.kind || 'stock';
    const pageServices = servicesFor(P.skip);
    // What the page currently sells: the selected variant to begin with, then whatever the configurators report as
    // slots and add-ons change.
    let amount = P.economy.amount;
    let weight = P.economy.weight;
    const weightCap = P.economy.weight_cap;

    // Economy, for THIS product. Weight decides it wherever we know a real one - the configurators add it up from the
    // cities and the add-ons - and the value threshold decides it otherwise. A market with no cap has no Economy to
    // judge, and the service is simply absent from the country's services.
    const economyFits = () => {
      // Weight first, and on its own merits: every market that sells Economy sells it under 2 kg, so a real weight
      // answers the question exactly. A weight of 0 means nobody set one, so it proves nothing.
      if (weight) return weight <= weightCap && (D.economy_cap == null || amount == null || amount <= D.economy_cap);
      // No weight to go on: the value band decides, which is exactly right in AE and KR and a stand-in elsewhere.
      if (D.economy_cap == null || amount == null) return true;
      return amount <= D.economy_cap;
    };
    const windowOf = (s, now) => {
      const [a, b] = shipWindow(kind, cutoffOf(s.carrier), now);
      return [arrive(a, s.days[0], s.weekdays), arrive(b, s.days[s.days.length - 1], s.weekdays)];
    };
    // "Or as early as ... with UPS Express": the day Express would arrive, only where it is sooner than every cheaper
    // service could arrive - not just the line's own - and at least two days before the line's last day. After the DHL
    // cut-off UPS Standard still goes the same day, so it reaches the day Express does, and offering Express there said
    // nothing (Alex, 2026-10-23: "it's the same day"). Was: compared with the lead service alone (2026-10-17).
    const expressDay = (services, lead, express, last, now) => {
      if (!express || express === lead) return null;
      const [xFirst, xLast] = windowOf(express, now);
      const soonest = Math.min(...services.filter((s) => !s.express).map((s) => windowOf(s, now)[0].getTime()));
      return (last - xLast) / DAY >= 2 && xFirst.getTime() < soonest ? xFirst : null;
    };

    window.cfShipEstimate = () => estimate(kind);

    // The hour only matters while the wait is short (Alex, 2026-10-21): above a week the sentence says "today", which
    // is the true promise. The order day keeps rolling at the cut-off either way.
    const LONG_LEAD_DAYS = 7;
    const render = () => {
      const now = berlinNow();
      // Asked on every render: a variant switch or a builder's weight can take the product past Economy's band and
      // back (until 2026-10-23 it was decided once at load, so the row and the arrival line never followed).
      const services = pageServices.filter((s) => s.key !== 'ups_economy' || economyFits());
      // The service the line names: the cheapest (the first in price order, Express aside) - or one just as fast (the
      // same transit days) that arrives sooner, because its carrier's cut-off is later: in Germany between 11 and 13,
      // UPS Standard instead of DHL. From stock its carrier's cut-off is the one line 1 counts down to; made to order
      // (freight too) the UPS hour, the order day every carrier shares.
      const nonExpress = services.filter((s) => !s.express);
      const sameSpeed = nonExpress.filter((s) => nonExpress[0] && s.days.join() === nonExpress[0].days.join());
      const ranked = sameSpeed.map((s, i) => ({ s, i, w: windowOf(s, now) }))
        .sort((x, y) => x.w[0] - y.w[0] || x.w[1] - y.w[1] || x.i - y.i);
      const lead = ranked.length ? ranked[0].s : services[0];
      const express = services.find((s) => s.express);
      const p = production(kind);
      // Made to order the order day is the UPS hour's for every carrier, so that is the hour line 1 counts down to.
      const cutoff = p ? cutoffOf('ups') : cutoffOf(!lead ? cutoffCarrier(kind, pageServices) : lead.carrier);
      const longLead = !!p && p[0] >= LONG_LEAD_DAYS;
      const hourly = counting(now, cutoff) && !longLead;
      const left = cutoff * 60 - now.minutes;
      const clock = { hours: Math.floor(left / 60), minutes: left % 60 };

      // Line 1: from stock the countdown to the day it is sent out, made to order what is made and in how many days.
      let status;
      let shipLine = '';
      if (!p) {
        const ship = orderDay(now, cutoff);
        if (iso(ship) === iso(now.today)) status = fill(P.t.countdown, { ...clock, day: P.t.day_today });
        else if (iso(ship) === iso(plus(now.today, 1))) status = fill(P.t.ready, { day: P.t.day_tomorrow });
        else status = fill(P.t.ready, { day: fill(P.t.day_on, { day: weekday.format(ship) }) });
      } else if (P.dated) {
        status = fill(P.t.lead[kind], { min: p[0], max: p[1] });
      } else {
        const [a, b] = shipWindow(kind, cutoff, now);
        const vars = { ...clock, from: whole(one(a)), to: whole(one(b)), date: whole(one(a)), it: kind === 'grand' ? P.t.it_n : P.t.it_m };
        // Both ends on the same workshop day: "will ship on Mon 26 Oct" (Alex, 2026-10-20).
        const on = iso(a) === iso(b);
        // A date ending in its own full stop ("lun. 2 nov.") ends the sentence: no second one.
        const tidy = (t) => t.replace(/\.(<\/span>(?:<\/b>)?)\./g, '.$1');
        if (P.order_subject) {
          // Several products in one order (the Set, the Individual Collection): the ship line is line 1.
          status = tidy(fill(on ? (hourly ? P.t.ship_countdown_order_on : P.t.ship_today_order_on)
            : (hourly ? P.t.ship_countdown_order : P.t.ship_today_order), vars));
        } else {
          let ship = fill(on ? (hourly ? P.t.ship_countdown_on : P.t.ship_today_on)
            : (hourly ? P.t.ship_countdown : P.t.ship_today), vars);
          // On the FRAME the ship dates are plain; only the arrival dates are bold (Alex, 2026-10-20).
          if (kind === 'frame') ship = ship.replace(`<b>${vars.from}</b>`, vars.from).replace(`<b>${vars.to}</b>`, vars.to).replace(`<b>${vars.date}</b>`, vars.date);
          // What is made on line 1, when it ships on line 2 (Alex, 2026-10-21).
          status = fill(P.t.lead[kind], { min: p[0], max: p[1] });
          shipLine = tidy(ship);
        }
      }
      document.querySelectorAll('[data-cf-delivery="status"]').forEach((el) => { el.innerHTML = status; });

      // Line 2: the arrival where the country has dates, the ship window where it does not.
      let text = shipLine;
      if (P.dated) {
        if (!p) {
          // From stock line 1 has the countdown, so line 2 is the arrival alone.
          if (lead) {
            const [first, last] = windowOf(lead, now);
            text = fill(P.t.arrival, { range: whole(span(first, last)), service: lead.name });
            const xDay = expressDay(services, lead, express, last, now);
            if (xDay) text = fill(P.t.arrival_express, { range: whole(span(first, last)), date: whole(one(xDay)), service: express.name });
          }
        } else if (isFreight(kind)) {
          const f = Array.isArray(D.freight) ? D.freight : [];
          if (f.length) {
            const [a, b] = shipWindow(kind, cutoff, now);
            const wk = [1, 2, 3, 4, 5];
            const range = whole(span(arrive(a, f[0], wk), arrive(b, f[f.length - 1], wk)));
            text = fill(hourly ? P.t.arrive_countdown_freight : P.t.arrive_today_freight, { ...clock, range });
          }
        } else if (lead) {
          const [first, last] = windowOf(lead, now);
          const xDay = expressDay(services, lead, express, last, now);
          const extra = xDay ? fill(P.t.arrive_express_tail, { date: whole(one(xDay)), service: express.name }) : '';
          text = fill(hourly ? P.t.arrive_countdown : P.t.arrive_today, { ...clock, range: whole(span(first, last)), extra });
        }
        text = text.replace(/\.(<\/span>(?:<\/b>)?)\./g, '.$1');
        // THE LINK GOES TO THE SERVICES, NOT TO THE TOP OF THE SHEET (Alex, 2026-10-24). #product-info-tabs is
        // caught on every product page by snippets/shipping-panel-link, which finds the Good to know Delivery fact
        // (or the page's own shipping accordion), OPENS IT where it is a tab on a phone, and scrolls to it. Landing
        // on the section itself put the reader on Dimensions - its first fact, and the first tab on a phone - which
        // is not what the words promise. None where the page has neither panel nor tabs, as before.
        //
        // And the words say what is there: "All delivery options" where this page sells several, "Learn more" where
        // it sells one, since a list of one is not a choice.
        const more = document.querySelector('[data-shipping-panel]')
          || document.getElementById('product-info-tabs')
          || document.querySelector('.shopify-section--good-to-know');
        const offered = services.filter((s) => pageServices.some((p) => p.key === s.key)).length;
        if (text && more) {
          text += ` <a href="#product-info-tabs" class="link">${offered > 1 ? P.t.all_options : P.t.learn_more}</a>`;
        }

        // Good to know's Delivery fact: every service keeps its row; one that does not apply to this product is
        // hidden rather than emptied, and one this page's shipping line never uses (DHL for a FRAME carton) too.
        const shown = new Set(services.map((s) => s.key));
        const served = new Set(pageServices.map((s) => s.key));
        pageServices.forEach((s) => {
          const [first, last] = windowOf(s, now);
          document.querySelectorAll(`[data-cf-delivery-service="${s.key}"]`).forEach((el) => {
            el.hidden = !shown.has(s.key);
            if (el.hidden) return;
            el.innerHTML = fill(P.t.service_line, { service: s.name, dates: whole(span(first, last)) });
          });
        });
        document.querySelectorAll('[data-cf-delivery-service]').forEach((el) => {
          if (!served.has(el.dataset.cfDeliveryService)) el.hidden = true;
        });
        // The line breaks: snippets/delivery-time puts each row's break inside its span, and writing innerHTML above
        // threw them away. Put back here, once the hidden rows are known: a paragraph that starts with its first row
        // opens without a break, one that starts with "Estimated delivery (Germany)" gives every row its own.
        document.querySelectorAll('.cf-delivery-lines').forEach((box) => {
          const rows = box.querySelectorAll('[data-cf-delivery-service]');
          if (!rows.length) return;
          let opened = box.firstChild !== rows[0];
          rows.forEach((el) => {
            if (el.hidden) return;
            if (opened) el.insertAdjacentHTML('afterbegin', '<br>');
            opened = true;
          });
        });
      }
      // An empty line 2 hides, or the row gap would show under line 1 (2026-10-21).
      document.querySelectorAll('[data-cf-delivery="arrival"]').forEach((el) => { el.innerHTML = text; el.hidden = !text; });
    };

    // The theme's variant picker says what is selected now; the configurators report their basket instead.
    document.addEventListener('variant:change', (e) => {
      const v = e.detail && e.detail.variant;
      if (v && v.id != null) window.cfDeliverySetVariant(v.id);
    });
    window.cfDeliveryRender = render;
    window.cfDeliverySetKind = (k) => { kind = k || P.kind || 'stock'; render(); };
    // A variant id where the product sent a map (anything whose variants straddle a limit), or an explicit weight in
    // grams and amount in minor units, which the configurators send once they have added up cities and add-ons.
    window.cfDeliverySetVariant = (id) => {
      const v = P.economy.variants && P.economy.variants[String(id)];
      if (!v) return;
      amount = v.amount;
      weight = v.weight;
      render();
    };
    window.cfDeliverySetBasket = (grams, minor) => {
      weight = typeof grams === 'number' ? grams : null;
      if (typeof minor === 'number') amount = minor;
      render();
    };
    render();
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', render);
    setInterval(render, 60000);
  };

  window.cfShip = { estimate: (kind) => estimate(kind), line, arrival, now: berlinNow };
})();
